import { useEffect, useState, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { verifyResetToken, setPassword, ApiError } from '../lib/api';

const shell: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--p-background,#f6f7f9)', fontFamily: 'var(--font-product)', padding: '48px 16px',
};
const card: React.CSSProperties = {
  width: 400, maxWidth: '100%', background: '#fff', border: '1px solid var(--p-border)',
  borderRadius: 12, boxShadow: 'var(--p-shadow-lg)', padding: 28,
};

export const SetPasswordPage = () => {
  const { token } = useParams();
  const [state, setState] = useState<'loading' | 'valid' | 'invalid' | 'done'>('loading');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    verifyResetToken(token)
      .then((r) => { setEmail(r.email); setState('valid'); })
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== pw2) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await setPassword(token!, pw);
      setState('done');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={shell}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <img src="/assets/5axis-icon.svg" alt="5 Axis" style={{ width: 26, height: 26 }} />
          <div style={{ fontWeight: 700, fontSize: 15 }}>5 Axis Billing</div>
        </div>

        {state === 'loading' && <div style={{ color: 'var(--p-muted-fg)' }}>Checking link…</div>}

        {state === 'invalid' && (
          <>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Link expired</h2>
            <p style={{ color: 'var(--p-muted-fg)', fontSize: 14 }}>
              This link is invalid or has expired. Ask an admin to resend your invite, or use “Forgot password” on the sign-in page.
            </p>
            <Link to="/" className="btn" style={{ marginTop: 8, display: 'inline-block' }}>Go to sign in</Link>
          </>
        )}

        {state === 'done' && (
          <>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Password set ✓</h2>
            <p style={{ color: 'var(--p-muted-fg)', fontSize: 14 }}>You can now sign in with your new password.</p>
            <Link to="/" className="btn btn--primary" style={{ marginTop: 8, display: 'inline-block' }}>Sign in</Link>
          </>
        )}

        {state === 'valid' && (
          <>
            <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Set your password</h2>
            <p style={{ color: 'var(--p-muted-fg)', fontSize: 13, marginTop: 0 }}>for {email}</p>
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input className="select" type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
              <input className="select" type="password" placeholder="Confirm password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
              {error && <div style={{ color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}
              <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? 'Saving…' : 'Set password'}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
